<template>

	<div class="@container flex-1 grid grid-cols-12 min-h-0 max-w-6xl">

		<!--
		Child components will be rendered here based on the route
		This also means the router-view needs to catch all emitted events of any of the children
		-->
		<router-view
			v-if="navigateByRoute"
			@selectAuthor="handleAuthorSelection"
			@selectAuthorGroup="handleAuthorGroupSelection"
			@createOwnVersion="$emit('createOwnVersion', $event)"
			@selectLessonPlan="$emit('selectLessonPlan', $event)"
		/>

		<!--
		Non-route-based rendering fallback of those same components (e.g. when used inside a drawer or modal).
		This also requires different implementation of 'go back' buttons as the default ones rely on route navigation.
		-->
		<template v-else>
			<LessonPlanDiscoverAuthor
				v-if="selectedAuthorId"
				:authorId="selectedAuthorId"
				:section="selectedAuthorSection ?? undefined"
				@selectAuthor="handleAuthorSelection"
				@createOwnVersion="$emit('createOwnVersion', $event)"
				@selectLessonPlan="$emit('selectLessonPlan', $event)"
			>
				<template #header>
					<div class="flex items-center px-6 pt-6 flex-shrink-0">
						<button class="btn btn-ghost btn-sm capitalize" @click="handleAuthorSelection(null)">
							<ArrowLeftIcon class="w-4 h-4"/>
							{{ $t('Return') }}
						</button>
					</div>
				</template>
			</LessonPlanDiscoverAuthor>

			<LessonPlanDiscoverAuthorGroup
				v-else-if="selectedAuthorGroup"
				:groupId="selectedAuthorGroup"
				@selectAuthorGroup="handleAuthorGroupSelection"
				@selectAuthor="handleAuthorSelection"
			>
				<template #header>
					<div class="flex items-center px-6 pt-6 flex-shrink-0">
						<button class="btn btn-ghost btn-sm capitalize" @click="handleAuthorGroupSelection(null)">
							<ArrowLeftIcon class="w-4 h-4"/>
							{{ $t('Return') }}
						</button>
					</div>
				</template>
			</LessonPlanDiscoverAuthorGroup>

			<LessonPlanDiscoverHome
				v-else
				@selectAuthor="handleAuthorSelection"
				@selectAuthorGroup="handleAuthorGroupSelection"
			/>
		</template>

	</div>
</template>


<script lang="ts">
import { useLessonPlanCollectionsFromColleaguesStore, usePublicLessonPlanCollectionsStore, authorGroupProperties } from '@/stores/DiscoverCollections.store';
import { LessonPlan } from '@/models/LessonPlan.model';
import LessonPlanDiscoverAuthorGroup from '@/components/LessonPlanDiscoverAuthorGroup.vue';
import SmaSyLocalStorage from '@/utils/SmaSyLocalStorage.util';
import LessonPlanDiscoverAuthor from '@/components/LessonPlanDiscoverAuthor.vue';
import LessonPlanDiscoverHome from '@/components/LessonPlanDiscoverHome.vue';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';


// Key for storing recently viewed authors in local storage
const RECENTLY_VIEWED_KEY = 'discover_recently_viewed_authors';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;


export default {
	name: 'LessonPlanDiscover',

	components: {
		LessonPlanDiscoverAuthor,
		LessonPlanDiscoverAuthorGroup,
		LessonPlanDiscoverHome,
		ArrowLeftIcon,
	},

	emits: ['selectLessonPlan', 'createOwnVersion'],

	props: {
		// Whether or not to use route navigation for author/author group selection.
		// If false, the component will manage the selected author/group in its own state and render the corresponding component
		// directly instead of relying on the router-view. This is useful for contexts like a drawer/modal where you don't want
		// the main route to change when navigating through authors/groups.
		navigateByRoute: {
			type: Boolean,
			default: true,
		},
	},

	data() {
		return {
			selectedAuthorId: null as string | null,
			selectedAuthorSection: null as string | null, // 'colleagues' or 'public'
			selectedAuthorGroup: null as string | null,
			authorGroupProperties,
		};
	},

	computed: {
		collectionsFromColleaguesStore() {
			return useLessonPlanCollectionsFromColleaguesStore();
		},

		publicCollectionsStore() {
			return usePublicLessonPlanCollectionsStore();
		},
	},

	async mounted() {
		await Promise.all([
			this.collectionsFromColleaguesStore.loadOnce(),
			this.publicCollectionsStore.loadOnce(),
		]);
	},

	methods: {
		handleAuthorSelection(userId: string | null, section?: string) {

			if(!userId) {
				// If null is emitted, treat it as a "go back to previous view" action
				if(this.$router.getRootComponentName() === 'LessonPlanLibrary') {
					if(this.selectedAuthorGroup) {
						this.$router.push({ name: 'lesson-plan-library.discover.group', params: { groupId: this.selectedAuthorGroup } });
					} else {
						this.$router.push({ name: 'lesson-plan-library.discover.home' });
					}
				} else {
					this.selectedAuthorId = null;
					// don't reset selectedAuthorGroup so that, if still set, the group view is rendered again
				}
				return;
			}

			this.recordRecentlyViewedAuthor(userId, section);

			if (this.$router.getRootComponentName() === 'LessonPlanLibrary') {
				if(section === 'colleagues') {
					this.$router.push({ name: 'lesson-plan-library.discover.colleague', params: { userId: userId } });
				} else if(section === 'public') {
					this.$router.push({ name: 'lesson-plan-library.discover.author', params: { userId: userId } });
				} else {
					console.warn('Unknown section for author selection, defaulting to public route', section);
					this.$router.push({ name: 'lesson-plan-library.discover.home' });
				}
			} else {
				this.selectedAuthorId = userId;
				this.selectedAuthorSection = section || null;
			}
		},

		handleAuthorGroupSelection(groupId: string | null) {
			this.selectedAuthorId = null;

			if(!groupId) {
				// If null is emitted, treat it as a "go back to main discover view" action
				if(this.$router.getRootComponentName() === 'LessonPlanLibrary') {
					this.$router.push({ name: 'lesson-plan-library.discover.home' });
				} else {
					this.selectedAuthorGroup = null;
				}
				return;
			}

			if(!this.isValidAuthorGroup(groupId)) {
				console.warn('Invalid author group selected: ' + groupId);
				return;
			}

			if (this.$router.getRootComponentName() === 'LessonPlanLibrary') {
				this.$router.push({ name: 'lesson-plan-library.discover.group', params: { groupId: groupId } });
			} else {
				this.selectedAuthorGroup = groupId;
			}
		},

		isValidAuthorGroup(group: string): boolean {
			return ['colleagues', 'publishers'].includes(group);
		},

		createLessonPlanFromTemplate(lessonPlan: LessonPlan) {
			this.$emit('createOwnVersion', lessonPlan);
		},

		recordRecentlyViewedAuthor(userId: string, section?: string) {
			const cutoff = Date.now() - THIRTY_DAYS_MS;
			let entries: { userId: string; section: string; viewedAt: number }[] =
			SmaSyLocalStorage.getCurrentUserItem(RECENTLY_VIEWED_KEY) || [];

			// Remove stale entries and any existing entry for this owner
			entries = entries.filter(
				(e) =>
					e.viewedAt >= cutoff 					// retain entries newer than [cutoff] days
					&&
					(
						e.userId !== userId 			// remove older entries for this user ...
						|| e.section !== (section || '')	// ... unless the section is different, allowing the same user to be stored separately
															// for different sections (e.g. colleagues vs authors)
					)
			);
			entries.unshift({ userId, section: section || '', viewedAt: Date.now() });
			SmaSyLocalStorage.setCurrentUserItem(RECENTLY_VIEWED_KEY, entries);
		},

	},
};
</script>
